import { Box, DialogActions, Paper, Stack, Typography } from '@mui/material';
import Button from '@mui/material/Button';
import { GridToolbar, useGridApiRef } from '@mui/x-data-grid';
import { randomId } from '@mui/x-data-grid-generator';
import { DataGridPremium } from '@mui/x-data-grid-premium';
import _ from 'lodash';
import { useEffect, useState } from 'react';
import FieldNames from '../../../defaults/FieldNames.json';
import GridDefinitions from '../../../defaults/GridDefinitions.json';
import ErrorService from '../../../services/ErrorService';
import FeatureService from '../../../services/FeatureService';
import FundingService from '../../../services/FundingService';
import { establishGridColumns } from '../../../utils/GridHelper';
import InFormGenericModal from '../../InFormGenericModal';
import ColorConstants from '../../../defaults/ColorConstants';
import { getLogForFieldUpdatesOnExistingFeature } from '../../../utils/AuditLogHelper';
import AuditLogService from '../../../services/AuditLogService';
import { isFundingCellEditable } from '../../../utils/FundingHelper';
import { setShowGlobalLoader } from '@store/slices/globalContextSlice';
import { useDispatch } from 'react-redux';

export default function FundingAcresPopup({
    openFlag,
    handleClearFundingAcres,
    isApproved,
    fundingRows,
    editableFeature,
    userInfo,
    areFundingGridsLoading,
    setAreFundingGridsLoading,
    featureUnsavedFields,
    setSnackbarMessage,
    setSnackbarOpen,
    setSnackbarSeverity,
    setFundingRows,
}) {
    const dispatch = useDispatch();

    const apiRef = useGridApiRef();

    const [acresAbortController, setAcresAbortController] = useState(null);
    const [acreageRowModesModel, setAcreageRowModesModel] = useState({});
    const [acresGridRows, setAcresGridRows] = useState([]);
    const [acresGridColumns, setAcresGridColumns] = useState([]);
    const [rowsSummedAcres, setRowsSummedAcres] = useState(0);
    const [negativeAcres, setNegativeAcres] = useState(false);

    const disableAcresSave =
        !rowsSummedAcres ||
        rowsSummedAcres !== editableFeature?.attributes[FieldNames.featureFields.calculatedArea];

    const areAllRowsContributed =
        acresGridRows?.length > 0 &&
        acresGridRows.filter(
            (x) =>
                x[FieldNames.fundingFields.fundingSourceProgram] ===
                FieldNames.fundingSourcePrograms.contributed
        ).length === acresGridRows?.length;

    const acresGridColDefinitions = [
        GridDefinitions.ManageCostsFundingAcresGrid.fundingSource,
        GridDefinitions.ManageCostsFundingAcresGrid.fundingAgency,
        GridDefinitions.ManageCostsFundingAcresGrid.fundingRegion,
        GridDefinitions.ManageCostsFundingAcresGrid.fundingUnit,
        GridDefinitions.ManageCostsFundingAcresGrid.fundingSubUnit,
        GridDefinitions.ManageCostsFundingAcresGrid.fundingTribe,
        GridDefinitions.ManageCostsFundingAcresGrid.acres,
    ];

    useEffect(() => {
        const colOverrideDefinitions = {
            valueParser: gridColParser,
            renderCell: gridColRenderer,
        };
        establishGridColumns(acresGridColDefinitions, setAcresGridColumns, colOverrideDefinitions);

        return () => {
            if (acresAbortController != null) {
                acresAbortController.abort();
            }
        };
    }, []);

    const gridColParser = (value, row, column, apiRef) => {
        if (
            column?.field === FieldNames.fundingAcreageFields.acres &&
            (value?.length > 0 || value > 0)
        ) {
            return Math.floor(Number(value));
        }
        return value;
    };

    const gridColRenderer = (params) => {
        if (isCellEditable(params) && params.id !== 'auto-generated-group-footer-root') {
            return (
                <Stack
                    direction={'row'}
                    justifyContent={'end'}
                    sx={{
                        width: '99%',
                        height: '100%',
                        border: 1,
                        alignItems: 'right',
                        color: params.value < 0 ? 'red' : 'inherit',
                    }}
                >
                    {params.value}
                </Stack>
            );
        }
        return <>{params.value}</>;
    };

    const isCellEditable = (params) => {
        // Make acres non-editable for point features
        return isFundingCellEditable(params, editableFeature, areAllRowsContributed);
    };

    useEffect(() => {
        if (openFlag && fundingRows?.length > 0) {
            if (!acresAbortController) {
                const newController = new AbortController();
                setAcresAbortController(newController);
            }

            const fundingRowsClone = _.cloneDeep(fundingRows);
            setAcresGridRows(fundingRowsClone);
            const gridRowsAcresOnly = fundingRowsClone.map(
                (row) => row[FieldNames.fundingAcreageFields.acres]
            );
            setNegativeAcres(gridRowsAcresOnly.filter((x) => x < 0).length > 0);
            const totalAcres = gridRowsAcresOnly.reduce((a, b) => a + b, 0);
            setRowsSummedAcres(totalAcres);
        } else if (!openFlag && acresAbortController != null) {
            acresAbortController.abort();
        }
    }, [openFlag, fundingRows]);

    const handleAcreageRowModesModelChange = (newRowModesModel) => {
        setAcreageRowModesModel(newRowModesModel);
    };

    const handleSaveFundingAcresRows = async (e, isNew) => {
        if (isApproved) {
            return;
        }

        try {
            dispatch(setShowGlobalLoader(true));
            setAreFundingGridsLoading(true);
            const updatedGridRows = apiRef.current.getSortedRows();
            let relevantUpdates = updatedGridRows.map((row) => {
                let rowObj = {};
                rowObj[FieldNames.featureFields.objectID] = row[FieldNames.featureFields.objectID];
                rowObj[FieldNames.fundingAcreageFields.acres] =
                    row[FieldNames.fundingAcreageFields.acres];
                return rowObj;
            });

            const negatives = relevantUpdates.filter(
                (r) => r[FieldNames.fundingAcreageFields.acres] < 0
            );

            if (negatives.length > 0) {
                throw new Error('Negative acre values are not allowed');
            }

            await FundingService.updateFundingEntries(relevantUpdates);

            setSnackbarSeverity('success');
            setSnackbarMessage('Funding Acres updated.');
            setSnackbarOpen(true);

            try {
                editableFeature.attributes[FieldNames.featureFields.isFundingAcresManual] =
                    FieldNames.yesNo.yes;
                await FeatureService.updateFeatureAttributes([editableFeature.attributes]);
                const changeString = getLogForFieldUpdatesOnExistingFeature(featureUnsavedFields);
                const finalChangeString = `Funding Row Acreage Updated. ${changeString}`;
                const auditResponse = await AuditLogService.addAuditLogEntry(
                    editableFeature.attributes,
                    editableFeature.attributes[FieldNames.featureFields.joinID],
                    editableFeature.attributes[FieldNames.featureFields.joinFeature],
                    userInfo[FieldNames.userFields.nifcUsername],
                    finalChangeString
                );
            } catch (error) {
                ErrorService.addErrorLogEntry(userInfo[FieldNames.userFields.nifcUsername], error);
                setSnackbarSeverity('warning');
                setSnackbarMessage(
                    'Funding Acres updated, but unable to update manual funding acres flag on related action'
                );
            } finally {
                setFundingRows(updatedGridRows);
            }
        } catch (error) {
            ErrorService.addErrorLogEntry(userInfo[FieldNames.userFields.nifcUsername], error);
            setSnackbarSeverity('error');
            setSnackbarMessage('Funding Acres update failed!');
            setSnackbarOpen(true);
        } finally {
            setAreFundingGridsLoading(false);
            handleClearFundingAcres();
            dispatch(setShowGlobalLoader(false));
        }
    };

    return (
        <InFormGenericModal
            openFlag={openFlag}
            closeMethod={handleClearFundingAcres}
            titleText={'Edit Funding Acres'}
            displayButton={false}
            escapeCanCloseModal={false}
        >
            <Box
                style={{
                    maxHeight: '75vh',
                    overflow: 'auto',
                    width: '75vw',
                }}
                sx={{
                    '& .MuiDataGrid-cell': {
                        bgcolor: (theme) => `${ColorConstants.MidLightGrayAlien}`,
                    },
                    '& .MuiDataGrid-cell--editable': {
                        bgcolor: (theme) => `${ColorConstants.WhiteAlien}`,
                    },
                }}
            >
                <Paper elevation={6} style={{ padding: 20, margin: 20 }}>
                    <h2>Update Funding Acres</h2>
                    <DataGridPremium
                        showToolbar
                        apiRef={apiRef}
                        loading={areFundingGridsLoading}
                        rows={acresGridRows}
                        columns={acresGridColumns}
                        getRowId={(row) =>
                            row[FieldNames.featureFields.id]
                                ? row[FieldNames.featureFields.id]
                                : randomId()
                        }
                        editMode='row'
                        initialState={{
                            aggregation: {
                                model: {
                                    [FieldNames.fundingAcreageFields.acres]: 'sum',
                                    [FieldNames.fundingAcreageFields.fundingAcreage]: 'sum',
                                },
                            },
                            columns: {
                                columnVisibilityModel: {
                                    featureObjectId: false,
                                },
                            },
                        }}
                        isCellEditable={(params) => {
                            if (isCellEditable(params)) {
                                return true;
                            }
                            return false;
                        }}
                        rowModesModel={acreageRowModesModel}
                        onRowModesModelChange={handleAcreageRowModesModelChange}
                        slots={{
                            toolbar: GridToolbar,
                        }}
                        processRowUpdate={(updatedRow) => {
                            apiRef.current.updateRows([updatedRow]);
                            const updatedGridRows = apiRef.current.getSortedRows();
                            const gridRowsAcresOnly = updatedGridRows.map(
                                (row) => row[FieldNames.fundingAcreageFields.acres]
                            );
                            const totalAcres = gridRowsAcresOnly.reduce((a, b) => a + b, 0);
                            setNegativeAcres(gridRowsAcresOnly.filter((x) => x < 0).length > 0);
                            setRowsSummedAcres(totalAcres);
                            return updatedRow;
                        }}
                    />
                    <Stack direction={'row'} justifyContent={'end'}>
                        <Box>
                            <Typography>
                                <b>Acres from Funding Rows: </b>
                                {rowsSummedAcres ?? 0}
                            </Typography>
                            <Typography>
                                <b>Calculated Acres on Action: </b>
                                {editableFeature?.attributes[
                                    FieldNames.featureFields.calculatedArea
                                ] ?? 0}
                            </Typography>
                        </Box>
                    </Stack>
                    <Stack direction={'row'} justifyContent={'end'}>
                        {disableAcresSave && (
                            <Typography color={'red'}>
                                Please make sure that the Acres from Funding Rows equal the
                                Calculated Acres for the Action
                            </Typography>
                        )}
                    </Stack>
                    <Stack direction={'row'} justifyContent={'end'}>
                        {negativeAcres && (
                            <Typography color={'red'}>
                                Negative acre values are not allowed, please update acreages
                            </Typography>
                        )}
                    </Stack>
                    <DialogActions>
                        <Button
                            color='primary'
                            variant='contained'
                            style={{ marginTop: '10px', marginRight: '15px', width: 100 }}
                            onClick={(e) => handleSaveFundingAcresRows(e)}
                            disabled={disableAcresSave || negativeAcres}
                        >
                            Save
                        </Button>
                        <Button
                            color='primary'
                            variant='contained'
                            style={{
                                marginTop: '10px',
                                marginLeft: '15px',
                                marginRight: '15px',
                                width: 100,
                            }}
                            onClick={handleClearFundingAcres}
                        >
                            Close
                        </Button>
                    </DialogActions>
                </Paper>
            </Box>
        </InFormGenericModal>
    );
}
